import { calculateMenuPosition } from './gridCalculations';

/**
 * Compute threshold-based color string for a cell value.
 * Returns 'green', 'yellow', 'orange', 'red', or null if no value/thresholds.
 */
export const computeAutoColor = (value, group, species, organisms = []) => {
  if (value === null || value === undefined || value === '') return null;

  const strValue = String(value).trim();
  if (strValue.startsWith('<') || strValue === '0' || strValue === '+') return 'green';

  const cleanValue = strValue.replace(/[\s\u00A0]/g, '').replace(/,/g, '.');
  const numValue = parseFloat(cleanValue);
  if (isNaN(numValue)) return null;
  if (numValue === 0) return 'green';

  const organism = organisms.find(o =>
    o.group === group && o.species === species && !o.isTotal
  );
  if (!organism || organism.threshold_red == null) return 'green';

  const tY = organism.threshold_yellow || 0;
  const tO = organism.threshold_orange || 0;
  const tR = organism.threshold_red || 0;

  if (numValue >= tR) return 'red';
  if (numValue >= tO) return 'orange';
  if (numValue >= tY) return 'yellow';
  return 'green';
};

/**
 * Update grid value, recalculate sums, and auto-set color from thresholds
 */
export const updateGridValueHelper = (organismKey, location, value, calculateSums, setGridData, organisms, setColorOverrides) => {
  setGridData(prev => {
    const updated = {
      ...prev,
      [organismKey]: {
        ...prev[organismKey],
        [location]: value
      }
    };
    
    // Auto-calculate sums
    return calculateSums(updated);
  });

  // Auto-compute color override from thresholds
  if (organisms && setColorOverrides) {
    const parts = organismKey.split('_');
    const group = parts[0] || '';
    const species = parts.slice(1).join('_') || '';
    const color = computeAutoColor(value, group, species, organisms);
    const overrideKey = `${organismKey}_${location}`;
    if (color) {
      setColorOverrides(prev => ({ ...prev, [overrideKey]: color }));
    } else {
      // Remove override for empty cells
      setColorOverrides(prev => {
        const updated = { ...prev };
        delete updated[overrideKey];
        return updated;
      });
    }

    // Also update sum row color if this organism belongs to a group with a sum row
    if (group === 'Kiselalger') {
      // Need to recalculate the sum for this location and set its color
      // We do this by reading the latest gridData after sums are calculated
      setTimeout(() => {
        setGridData(currentGrid => {
          const sumKey = `${group}_${group.toUpperCase()} SUM`;
          const sumValue = currentGrid[sumKey]?.[location];
          if (sumValue) {
            const sumColor = computeAutoColor(sumValue, group, `${group.toUpperCase()} SUM`, organisms);
            const sumOverrideKey = `${sumKey}_${location}`;
            if (sumColor) {
              setColorOverrides(prev => ({ ...prev, [sumOverrideKey]: sumColor }));
            }
          }
          return currentGrid; // Don't modify
        });
      }, 0);
    }
  }
};

/**
 * Update zooplankton value with normalization
 */
export const updateZooplanktonValueHelper = (organismKey, location, value, setZooplanktonGridData) => {
  // Normalize the value for storage
  let normalizedValue = value;
  
  // Handle special cases
  if (value === '<5' || value === '< 5' || value === '<5000' || value === '< 5000') {
    normalizedValue = '<5'; // Standardize to <5 format
  } else if (value === '+') {
    normalizedValue = '+'; // Keep + as-is (oppdaget, men ikke kvantifisert)
  } else if (value && value.trim()) {
    // Try to parse as number
    const numValue = value.replace(/,/g, '').trim();
    if (!isNaN(numValue) && numValue !== '') {
      normalizedValue = numValue;
    }
  }
  
  setZooplanktonGridData(prev => ({
    ...prev,
    [organismKey]: {
      ...prev[organismKey],
      [location]: normalizedValue
    }
  }));
};

/**
 * Handle right click on cell
 */
export const handleRightClickHelper = (e, organismKey, location, setContextMenu) => {
  e.preventDefault();
  e.stopPropagation(); // Prevent event from bubbling to row handler
  
  const { x, y } = calculateMenuPosition(e.clientX, e.clientY, 200, 300);
  
  setContextMenu({
    x,
    y,
    organismKey,
    location
  });
};

/**
 * Handle right click on row
 */
export const handleRowRightClickHelper = (e, organismKey, organism, setContextMenu) => {
  e.preventDefault();
  
  const { x, y } = calculateMenuPosition(e.clientX, e.clientY, 200, 100);
  
  setContextMenu({
    x,
    y,
    organismKey,
    isRowContext: true,
    organism
  });
};

/**
 * Set color override for cell
 */
export const setColorOverrideHelper = (contextMenu, color, setColorOverrides, setContextMenu) => {
  if (!contextMenu) return;
  
  const overrideKey = `${contextMenu.organismKey}_${contextMenu.location}`;
  setColorOverrides(prev => ({
    ...prev,
    [overrideKey]: color
  }));
  setContextMenu(null);
};

/**
 * Remove color override for cell
 */
export const removeColorOverrideHelper = (contextMenu, setColorOverrides, setContextMenu) => {
  if (!contextMenu) return;
  
  const overrideKey = `${contextMenu.organismKey}_${contextMenu.location}`;
  setColorOverrides(prev => {
    const updated = { ...prev };
    delete updated[overrideKey];
    return updated;
  });
  setContextMenu(null);
};

/**
 * Update depth for organism
 */
export const updateDepthHelper = (organismKey, newDepth, setDepthOverrides) => {
  setDepthOverrides(prev => ({
    ...prev,
    [organismKey]: newDepth
  }));
};
